/**
 * Shared Candidate Types and Interfaces
 *
 * Used by:
 * - CandidatesPage (main directory)
 * - CandidatesTab (workspace context)
 * - CandidatesReport (report context)
 */

import { LucideIcon } from 'lucide-react';
import type { FieldType, FieldConfig } from '../../types/custom-fields';

// =============================================================================
// Base Candidate Types
// =============================================================================

/**
 * One organization custom field value attached to a candidate.
 *
 * Definitions are org-scoped but values are workspace-scoped, so an org-level
 * report can carry several entries sharing a `definitionId` — one per
 * workspace. `workspaceName` is populated only in that org context.
 */
export interface CandidateCustomFieldValue {
  definitionId: string;
  name: string;
  fieldType: FieldType;
  fieldConfig?: FieldConfig;
  displayOrder?: number;
  workspaceId?: string;
  workspaceName?: string | null;
  value: unknown;
}

export type SocialPlatform = 'x' | 'instagram' | 'facebook' | 'linkedin' | 'bluesky' | 'youtube';

export interface SocialMediaLink {
  platform: SocialPlatform;
  handle: string;
  url: string;
}

export interface BaseCandidate {
  id: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string | null;
  officeSought: string;
  district?: string | null;
  partyAffiliation?: string | null;
  electionYear: number;
  status: string;
  isIncumbent: boolean;
  incumbentLegislatorId?: number | null;
  electionType?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  socialMedia?: SocialMediaLink[];
  activeStaffCount?: number;
  campaignManagerName?: string | null;
  campaignManagerEmail?: string | null;
  treasurerName?: string | null;
  incumbentChamber?: string | null;
  incumbentDistrict?: string | null;
  incumbentParty?: string | null;
  totalFundsRaised?: number | null;
  seatIncumbentName?: string | null;
  seatIncumbentId?: number | null;
  isPriority?: boolean;
  position?: 'Support' | 'Oppose' | 'Monitor' | 'Neutral';
  trackingNotes?: string;
  customFieldCount?: number;
  customFieldValues?: CandidateCustomFieldValue[];
}

export type TrackedCandidate = BaseCandidate;

export interface CandidateWorkspaceEntry {
  id: string;
  name: string;
  position?: string;
}

/** Org-level report candidate with aggregated workspace tracking */
export interface OrgCandidate extends BaseCandidate {
  workspaces?: CandidateWorkspaceEntry[];
}

// =============================================================================
// Filter Types
// =============================================================================

export interface CandidateFilterOptions {
  offices: string[];
  parties: string[];
  districts: { district: string; office: string }[];
  electionYears: number[];
  statuses: string[];
}

export interface CandidateFilterState {
  searchQuery: string;
  officeSought: string;
  party: string;
  district: string;
  electionYear: string;
  status: string;
}

export const DEFAULT_FILTER_STATE: CandidateFilterState = {
  searchQuery: '',
  officeSought: 'All',
  party: 'All parties',
  district: 'All districts',
  electionYear: new Date().getFullYear().toString(),
  // Candidates default to Active. The vocabulary comes from the role_statuses
  // table, so the exact casing is data rather than code — useCandidateFiltering
  // reconciles this against the fetched options once they arrive.
  status: 'active',
};

export interface CandidateFiltersConfig {
  showSearch?: boolean;
  showOffice?: boolean;
  showParty?: boolean;
  showDistrict?: boolean;
  showElectionYear?: boolean;
  showStatus?: boolean;
  showClearButton?: boolean;
  compact?: boolean;
}

export const DEFAULT_FILTERS_CONFIG: CandidateFiltersConfig = {
  showSearch: true,
  showOffice: true,
  showParty: true,
  showDistrict: true,
  showElectionYear: true,
  showStatus: true,
  showClearButton: true,
  compact: false,
};

// =============================================================================
// Sorting Types
// =============================================================================

export type CandidateSortField =
  | 'name'
  | 'office_sought'
  | 'district'
  | 'party'
  | 'election_year'
  | 'status'
  | 'total_funds_raised'
  | 'seat_incumbent_name';
export type SortDirection = 'asc' | 'desc';

export interface SortState {
  field: CandidateSortField;
  direction: SortDirection;
}

export const DEFAULT_SORT_STATE: SortState = {
  field: 'name',
  direction: 'asc',
};

// =============================================================================
// Data Source Types
// =============================================================================

export type CandidateDataSource =
  | 'all'
  | 'tracked'
  | 'report'
  | 'report-all';

export interface CandidateDataConfig {
  source: CandidateDataSource;
  workspaceId?: string;
}

// =============================================================================
// View Mode Types
// =============================================================================

export type ViewMode = 'table' | 'card';

// =============================================================================
// Table Configuration Types
// =============================================================================

export interface CandidateRenderContext {
  workspaceId?: string;
  isPublicView?: boolean;
  permissions?: {
    canView?: boolean;
    canEdit?: boolean;
    canManage?: boolean;
  };
  onNavigate?: (candidateId: string) => void;
  onBookmark?: (candidateId: string, name: string) => void;
  onPositionChange?: (candidateId: string, position: string) => void;
  onPriorityToggle?: (candidateId: string, isPriority: boolean) => void;
  onRemove?: (candidate: TrackedCandidate) => void;
  trackedCandidateIds?: Set<string>;
  selectedCandidateIds?: Set<string>;
  onSelectCandidate?: (candidateId: string, checked: boolean) => void;
}

// =============================================================================
// Card Configuration Types
// =============================================================================

export interface CandidateCardAction {
  id: string;
  label: string;
  icon: LucideIcon;
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  isActive?: (candidate: BaseCandidate, context: CandidateRenderContext) => boolean;
  onClick: (candidate: BaseCandidate, context: CandidateRenderContext) => void;
  hidden?: (candidate: BaseCandidate, context: CandidateRenderContext) => boolean;
}
